
import _ from 'lodash'
import { ACCESS_KEY, urlAPI } from '../../urls'
import { type AreaGroup, setAreas, setConfig, setConfigDBState, setConfigId, setEventsRunsHistory, setMicrophoneAccessProvided, setUserDevice } from '../slices/appSlice'
import requestMicrophoneAccess from '../../functions/shared/audio/requestMicrophoneAccess'
import { getErrorss, getMessageForError } from '../../functions/errors/GetErrors'
import QueryString from 'qs'
import { type Unit } from '../slices/unitsSlice'
import { AppDispatch } from '../store'
import { setLiveServicePipelines } from '../../components/adminModule/clientOrders/servicePipelines'

export const ALLOWED_UNIT_STATUSES = [
    "Building Management",
    "Keys in BSO office",
    "Digital lock",
    "Vacant",
    "Occupied",
    "Door open",
    "Door closed",
    "Agent Unit",
    "Landlord Unit"
]
export const fetchAppConfig = () => async (dispatch: AppDispatch) => {

    try {
        const jwt = window.localStorage.getItem(ACCESS_KEY) ?? ''
        const configBody = {
            method: 'GET',
            collection: 'config'
        }

        const configResponse = await fetch(`${urlAPI}`, {
            method: 'POST',
            body: JSON.stringify(configBody),
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${jwt}`
            }
        })

        const configJSON = await configResponse.json()
        if (!configJSON.success) {
            return {
                ...configJSON,
                isSuccessful: false,
                textNotification: getErrorss(configJSON)
            }
        }
        const attrs = configJSON.response?.data?.attributes ?? {}


        const servicePipelinesReady = setLiveServicePipelines(attrs?.ServicePipelines)
        dispatch(setConfig({
            ...(configJSON.response?.data?.attributes?.Selectors ?? {}),
            UnitReviewOptions: attrs?.UnitReviewOptions,
            ServicePipelines: servicePipelinesReady ? attrs?.ServicePipelines : undefined
        }))

      dispatch(setConfigId(configJSON.response?.data?.id ?? null))
      dispatch(setEventsRunsHistory(Array.isArray(attrs?.EventsRunsHistory) ? attrs.EventsRunsHistory : []))

        const areasConfig: AreaGroup[] = configJSON.response?.data?.attributes?.Areas ?? []
        const unitsQuery = {
            filters: {
                $and: [
                    {
                        Property: {
                            Area: {
                                $notNull: true,
                            }
                        }
                    },
                    {
                        $or: [
                            { Statuses: { $containsi: "Building Management" } },
                            { Statuses: { $containsi: "Keys in BSO office" } },
                            { Statuses: { $containsi: "Digital lock" } },
                            { Statuses: { $containsi: "Vacant" } },
                            { Statuses: { $containsi: "Occupied" } },
                            { Statuses: { $containsi: "Door open" } },
                            { Statuses: { $containsi: "Door closed" } },
                            { Statuses: { $containsi: "Agent Unit" } },
                            { Statuses: { $containsi: "Landlord Unit" } }
                        ]
                    },
                    {
                        Marketing: {
                            Use: {
                                "$notNull": true
                            }
                        }
                    },
                    {
                        User: {
                            "id": {
                                "$notNull": true
                            }
                        }
                    },
                    {
                        ManagedBy: {
                            "$notNull": true
                        }
                    },
                ]
            },
            populate: {
                Property: { fields: ["Area"] },
                Statuses: true,
                User: { fields: ["id"] },
            }
        }

        const unitsBody = {
            method: 'GET',
            collection: 'units',
            query: QueryString.stringify(unitsQuery, { encodeValuesOnly: true })
        }

        const unitsResponse = await fetch(`${urlAPI}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${String(jwt)}`
            },
            body: JSON.stringify(unitsBody)
        })

        const unitsJSON = await unitsResponse.json()
        const units: Unit[] = unitsJSON?.response?.data ?? []


        const areasWithCounts = buildAreasWithUnitCounts(units, areasConfig)
       
        dispatch(setAreas(areasWithCounts))


        return {
            ...configJSON,
            isSuccessful: true
        }

    } catch (err) {
        console.error(err)
        return {
            isSuccessful: false,
            textNotification: getErrorss(getMessageForError(err as any))
        }
    }
}

export const getMicrophoneAccess = () => async (dispatch: AppDispatch): Promise<{ isSuccessful: boolean }> => {
    const savedAccessProvided = window.localStorage.getItem('BSO_MICROPHONE_ACCESS_PROVIDED')
    if (savedAccessProvided === 'true' || savedAccessProvided === 'false') {
        dispatch(setMicrophoneAccessProvided(savedAccessProvided === 'true'))
        return {
            isSuccessful: true
        }
    } else {
        const accessProvided = await requestMicrophoneAccess()
        window.localStorage.setItem('BSO_MICROPHONE_ACCESS_PROVIDED', accessProvided.toString())
        dispatch(setMicrophoneAccessProvided(accessProvided))
        return {
            isSuccessful: true
        }
    }
}

export const getUserDevice = () => async (dispatch: AppDispatch): Promise<{ isSuccessful: boolean }> => {
    const userAgent = navigator.userAgent

    const isIPhone = _.includes(userAgent.toLowerCase(), 'iphone')
    const isAndroid = _.includes(userAgent.toLowerCase(), 'android')

    if (isIPhone) {
        dispatch(setUserDevice('IPhone'))
    } if (isAndroid) {
        dispatch(setUserDevice('Android'))
    }
    return {
        isSuccessful: true
    }
}



export const buildAreasWithUnitCounts = (units: Unit[], areasConfig: AreaGroup[]) => {
    return areasConfig.map((group) => {
        const count = units.filter((unit) => {
            const unitArea = unit.attributes?.Property?.data?.attributes?.Area
            const unitStatusArr = unit.attributes?.Statuses || []
            const hasAllowedStatus = unitStatusArr.some((status: string) =>
                ALLOWED_UNIT_STATUSES.includes(status)
            )

            return hasAllowedStatus && unitArea && (group.areas.includes(unitArea) || group.name.includes(unitArea))
        }).length

        return {
            ...group,
            unitsCount: count
        }
    })
}
